import {
  convertUTCDate,
  DATE_FORMAT,
  getFormattedDate,
  getFormattedDateTime,
} from './Date';
import {
  APPT_TYPES,
  DEFAULT_APPOINTMENT_PROPERTIES,
  EVENTS,
  OFFSITE_COSMETIC_SEARCH,
  DEFAULT_APPOINTMENT_COUNT_SEARCH_LIMIT,
} from '../data/Constants';

import { differenceInDays, addDays, isAfter } from 'date-fns';
import { transformEventsToAppointment } from '../data/AppointmentEventRules';
import { cleanEventsObjectForAPI } from './EventFn';
import { getUrl } from './Query';
import { getOrderingMode } from '../recoil/atom/OrderingMode';

export const prettifyAppointment = (appointment, providers, locations) => {
  const { FULL: fullDateFormat } = DATE_FORMAT;
  const { ApptStart, ProviderId, LocationId } = appointment;

  const provider = providers.find(
    (p) => parseInt(p.ProviderId) === parseInt(ProviderId),
  );
  const location = locations.find(
    (l) => parseInt(l.LocationId) === parseInt(LocationId),
  );

  const { Timezone = 'America/Chicago' } = location;

  if (typeof ApptStart === 'undefined') throw 'Missing appointment ApptStart';
  if (typeof ProviderId === 'undefined') throw 'Missing ProviderId';
  if (typeof LocationId === 'undefined') throw 'Missing LocationId';

  const appointmentDateTime = convertUTCDate(ApptStart, 'America/Chicago');
  const dateTimeFormatted = getFormattedDateTime(
    appointmentDateTime,
    fullDateFormat,
    Timezone,
  );

  return {
    ...appointment,
    provider,
    location,
    dateTimeFormatted,
  };
};

/**
 * Verify that the required variables exist inside of the appointment object
 * @param {array} appointments
 * @returns array
 */
export const verifyAppointmentResults = (appointments) => {
  return appointments.map((apt) => ({
    ...DEFAULT_APPOINTMENT_PROPERTIES,
    ...apt,
  }));
};

/**
 * @typedef {object} DatePreferenceObj
 * @property {date|string} DateRangeBegin
 * @property {date|string} DateRangeEnd
 * @property {string} TimeOfDay
 */

/**
 * @typedef {object} SearchParamsDateChunk
 * @property {DatePreferenceObj} DatePreference
 */

/**
 * Look through the search object and generate the array needed to search
 * with. This essentially duplicates the search params, but the dates are
 * separated so the search only runs in XXXX day increments.
 * @param {SearchParamsDateChunk} searchParams
 * @returns array of search param objects.
 */
export const chunkSearchParamsByDates = (searchParams) => {
  const { DatePreference } = searchParams;
  const { DateRangeBegin, DateRangeEnd, TimeOfDay } = DatePreference;

  const dtDateRangeBegin = new Date(DateRangeBegin);
  const dtDateRangeEnd = new Date(DateRangeEnd);

  // Set the number of days per search that we want
  const SEARCH_CHUNK = 7;

  let returnArray = [];

  // Calculate the difference between the begin and end search date
  const difference = differenceInDays(dtDateRangeEnd, dtDateRangeBegin);

  // Calculate the number of chunked searches we need
  const chunks = Math.ceil(difference / SEARCH_CHUNK);

  // Set the params for the loop.
  let flag = false;
  let currentBeginDate = dtDateRangeBegin;
  let currentEndDate = addDays(currentBeginDate, SEARCH_CHUNK);

  for (let i = 0; i < chunks || !flag; i++) {
    // Generate the begin and end dates, except for the first one because
    // that is already calculated for us.
    if (i !== 0) {
      currentBeginDate = addDays(currentEndDate, 1);
      currentEndDate = addDays(currentBeginDate, SEARCH_CHUNK);
    }

    // Make sure the current begin date never sneaks past the end of search
    if (isAfter(currentBeginDate, dtDateRangeEnd)) {
      flag = true;
      break;
    }

    // Check if this is the last iteration, which might be a shorter
    // search range than the number of days specified for each iteration.
    if (isAfter(currentEndDate, dtDateRangeEnd)) {
      currentEndDate = dtDateRangeEnd;
      flag = true;
    }

    returnArray.push({
      ...searchParams,
      DatePreference: {
        DateRangeBegin: currentBeginDate,
        DateRangeEnd: currentEndDate,
        TimeOfDay,
      },
      DateRangeBegin: new Date(currentBeginDate).toString(),
      DateRangeEnd: new Date(currentEndDate).toString(),
    });
  }

  return returnArray;
};

/**
 * Performs an appointment search based on provided properties and search parameters.
 * It processes chunks of search parameters, makes API calls to retrieve search results,
 * and aggregates the results. It also updates the search status throughout the process.
 * If the results exceed the integer value of DEFAULT_APPOINTMENT_COUNT_SEARCH_LIMIT, the search will stop and return the results obtained so far.
 * @async
 * @param {object} props - The properties object containing parameters and configurations for the appointment search.
 * @param {Array} props.locations - An array of location objects to be included in the search parameters.
 * @param {Array} props.providers - An array of provider objects to be included in the search parameters.
 * @param {Array} props.events - An array of event objects that describe the appointment details.
 * @param {string} props.patientOrOther - Indicator of whether the patient or another entity is the subject of the search.
 * @param {object} props.AxiosService - An Axios instance configured for making HTTP requests.
 * @param {string} props.SEARCH_URI - The URI endpoint for the search API.
 * @param {Function} [props.setStatus=() => {}] - An optional function to update the status of the search.
 * @param {Function} [props.setApptOptions=() => []] - An optional function to update appointments as they're found.
 * @param {boolean} [props.overrideFilters=false] - Boolean indicating whether to override default filters in the search.
 * @returns {Promise<Array>} A promise that resolves to an array of aggregated search results.
 */
export const performAppointmentSearch = async (props) => {
  const {
    locations, //
    providers,
    events,
    patientOrOther,
    AxiosService,
    SEARCH_URI,
    setStatus = () => {},
    setApptOptions = () => [],
    overrideFilters = false,
    apiVersion,
  } = props;

  // here is another version of events, except this one does not scrub results of metadata
  const Events = cleanEventsObjectForAPI({
    events,
    patientOrOther,
    doNotScrubResult: true,
  });

  const searchParamChunked = generateSearchParams({
    locations,
    providers,
    events,
    patientOrOther,
    overrideFilters,
    apiVersion,
  });

  let returnResults = [];

  for (let i = 0; i < searchParamChunked.length; i++) {
    const fStartDate = getFormattedDate(
      searchParamChunked[i].DateRangeBegin,
      DATE_FORMAT.SHORT_YEAR,
    );
    const fEndDate = getFormattedDate(
      searchParamChunked[i].DateRangeEnd,
      DATE_FORMAT.SHORT_YEAR,
    );

    if (!overrideFilters) {
      setStatus([
        'Thanks for your patience while we look for your appointment! It might take up to a minute, but we usually find one in under 15 seconds.',
        `Searching between ${fStartDate} and ${fEndDate}.`,
      ]);
    } else {
      setStatus([
        'Search found no results with the parameters you provided, widening search.',
        `Searching between ${fStartDate} and ${fEndDate}.`,
      ]);
    }

    const { data } = await AxiosService.post(getUrl(SEARCH_URI), {
      ...searchParamChunked[i],
      Events,
      enableEventRulesProcessing: 1,
      OrderingMode: getOrderingMode(),
    });

    const { results } = data;

    // Prep the results for joining with the rest of the appointments.
    const preppedData =
      typeof results === 'undefined' ? [] : verifyAppointmentResults(results);

    // Check if results is undefined or not.  If it is, then we most likely got a 204
    // with an error in the search returning nothing.
    returnResults = [
      ...returnResults, //
      ...preppedData,
    ];
    // Update setApptOptions, respect constant count
    setApptOptions((prevOptions) => {
      const combinedResults = [...prevOptions, ...preppedData];
      return combinedResults.slice(0, DEFAULT_APPOINTMENT_COUNT_SEARCH_LIMIT); // Limit results to default limit count
    });
    // Break if we make it above the count.
    if (returnResults.length >= DEFAULT_APPOINTMENT_COUNT_SEARCH_LIMIT) break;
  }

  return returnResults;
};

/**
 * Generates search parameters for appointment search based on given properties.
 * It transforms events into appointment parameters and cleans the events object for API use.
 * If `overrideFilters` is true, it will bypass certain filters in the transformation process.
 * The resulting search parameters are then chunked by dates for the search query.
 * @param {object} props - The properties object with parameters for generating search params.
 * @param {Array} props.locations - An array of location objects to be included in the search.
 * @param {Array} props.providers - An array of provider objects to be included in the search.
 * @param {Array} props.events - An array of event objects that describe the appointment details.
 * @param {string} props.patientOrOther - Indicator of whether the patient or another entity is the subject of the search.
 * @param {boolean} [props.overrideFilters=false] - Boolean indicating whether to override default filters.
 * @returns {Array} An array of search parameters chunked by dates.
 */
export const generateSearchParams = (props) => {
  const {
    locations,
    providers,
    events,
    patientOrOther,
    overrideFilters = false,
    apiVersion,
  } = props;

  // Generate the search params.
  const searchParams = transformEventsToAppointment({
    locations,
    providers,
    overrideFilters,
  })(cleanEventsObjectForAPI({ events, patientOrOther }));

  return chunkSearchParamsByDates(searchParams);
};

/**
 * Handles the search for appointments based on the event type.
 * If the appointment type is cosmetic, it performs a cosmetic appointment search.
 * Otherwise, it performs a regular appointment search.
 * @param {object} props - The properties object containing event details.
 * @param {Array} props.events - An array of event objects to be searched through.
 * @returns {Promise<object>} A promise that resolves to the search result object.
 */
export const handleAppointmentSearch = async (props) => {
  const { events } = props;
  const apptType = events.find(({ id }) => id === EVENTS.APPT_TYPE);

  // If the appointment is cosmetic, run that search.
  if (apptType.result === APPT_TYPES.COSMETIC)
    return await performCosmeticAppointmentSearch(props);
  // Else, perform the regular appointment search.
  else return await performAppointmentSearch(props);
};

/**
 * Performs a search for cosmetic appointments, including handling for special
 * cases such as offsite appointments.
 * @param {Object} props - The properties object containing the search parameters.
 * @param {Array<Object>} props.events - An array of event objects that may determine the logic of the search.
 * @param {Function} [props.setApptOptions=() => []] - An optional function to update appointments as they're found.
 * @returns {Array<Object>} An array of appointment search results.
 */
export const performCosmeticAppointmentSearch = async (props) => {
  const { events, setApptOptions = () => [] } = props;

  // Get the last seen duration screen
  const lastSeenDuration =
    events.find(({ id }) => id === EVENTS.LAST_SEEN_DURATION).result ?? '';

  // Get the prior approval screen
  const priorApproval =
    events.find(({ id }) => id === EVENTS.PRIOR_LASER_APPROVAL).result ?? '';

  // Get the selected purpose screen
  const purposeEvent =
    events.find(({ id }) => id === EVENTS.PURPOSE).result ?? [];

  // Run the primary search
  const primarySearch = performAppointmentSearch(props);

  // Prep the variable to contain the secondary search (if applicable)
  let secondarySearch = [];

  // Make sure this is not a CPA search. Only CC appointment types should
  // make it to the alternate search.
  if (!(priorApproval === 'Yes' && lastSeenDuration === 'Yes')) {
    // Check to see if we've established an offsite version of the cosmetic
    // purpose. If we have, then we need to perform a secondary search with
    // the offsite purpose ID as well.
    if (Object.hasOwn(OFFSITE_COSMETIC_SEARCH, purposeEvent[0].PurposeId)) {
      // Perform the secondary search, swapping the original purpose ID with
      // the one specified inside of the OFFSITE_COSMETIC_SEARCH object
      secondarySearch = performAppointmentSearch({
        ...props,
        events: events.map(({ id, result, ...items }) => ({
          id,
          result:
            id === EVENTS.PURPOSE
              ? [
                  {
                    ...result[0],
                    id: OFFSITE_COSMETIC_SEARCH[result[0].PurposeId],
                    PurposeId: OFFSITE_COSMETIC_SEARCH[result[0].PurposeId],
                  },
                ]
              : result,
          ...items,
        })),
      });
    }
  }

  // Fetch the primary and secondary results when the promise resolves.
  const [primaryResults, secondaryResults] = await Promise.all([
    primarySearch,
    secondarySearch,
  ]);

  // Append the 'offsite: true' property to the secondary results.
  const secondaryResults_appended = secondaryResults.map((itm) => ({
    ...itm,
    offsite: true,
  }));

  // Combine, trim, and sort the results.
  const combinedResults = [...primaryResults, ...secondaryResults_appended]
    .slice(0, DEFAULT_APPOINTMENT_COUNT_SEARCH_LIMIT)
    .sort((a, b) => {
      const aTime = new Date(a.ApptStartTimeStamp);
      const bTime = new Date(b.ApptStartTimeStamp);

      const aDate = new Date(a.ApptStart);
      aDate.setHours(aTime.getHours());
      aDate.setMinutes(aTime.getMinutes());
      aDate.setMilliseconds(aTime.getMilliseconds());

      const bDate = new Date(b.ApptStart);
      bDate.setHours(bTime.getHours());
      bDate.setMinutes(bTime.getMinutes());
      bDate.setMilliseconds(bTime.getMilliseconds());

      return bDate - aDate;
    });

  // avoid circular reference nonsense by not allowing state to be mutated
  if (secondaryResults_appended.length > 0) {
    const deepCloneArray = (arr) => JSON.parse(JSON.stringify(arr));
    const clonedResults = deepCloneArray(combinedResults);
    setApptOptions(clonedResults);
  }

  return combinedResults;
};
